<template>
  <div class="er-settings">
    <div class="er-card">
      <!-- Header with Save Button -->
      <div class="er-card-header">
        <h2><i class="fa fa-envelope-o"></i> Automated Email Reports</h2>
        <button class="er-btn er-btn-save" @click="handleSave" :disabled="saving">
          <i :class="saving ? 'fa fa-spinner fa-spin' : 'fa fa-save'"></i>
          {{ saving ? 'Saving...' : 'Save Email Report Settings' }}
        </button>
      </div>

      <div v-if="loading" class="text-center py-5">
        <b-spinner variant="primary" />
        <p class="mt-2" style="color: #6b7280;">Loading report settings...</p>
      </div>

      <div v-if="!loading">
        <!-- ─── DELIVERY SCHEDULE ─── -->
        <div class="er-divider-section">
          <h3 class="er-subsection-title"><i class="fa fa-clock-o"></i> Delivery Schedule</h3>
          <p class="er-help-text" style="margin-bottom: 8px;">
            <strong>Weekly</strong> reports are delivered at the end of the last working day of the week.
            <strong>Monthly</strong> reports are delivered at the end of the last working day of the month.
          </p>
          <div class="er-note-box">
            <div class="er-note-header"><i class="fa fa-exclamation-triangle"></i> <strong>Note:</strong></div>
            <p>If you select a time of delivery that is prior to completing the day, the data may not be accurate.</p>
            <p>Consider selecting a time that is after you've finished seeing all patients for the most accurate data.</p>
          </div>

          <div class="er-schedule-row">
            <div v-for="freq in frequencies" :key="freq.key" class="er-schedule-card" :class="{ 'is-disabled': !schedules[freq.key].IsActive }">
              <div class="er-schedule-card-top">
                <span class="er-schedule-card-label">{{ freq.label }}</span>
                <label class="er-toggle">
                  <input type="checkbox" v-model="schedules[freq.key].IsActive" />
                  <span class="er-toggle-slider"></span>
                  <span class="er-toggle-text">{{ schedules[freq.key].IsActive ? 'Enabled' : 'Disabled' }}</span>
                </label>
              </div>
              <div class="er-schedule-card-body" v-if="schedules[freq.key].IsActive">
                <div class="er-schedule-time-row">
                  <span class="er-form-label-inline">Deliver after</span>
                  <select v-model="schedules[freq.key].hour" class="er-form-select er-time-sel">
                    <option v-for="h in [4,5,6,7,8,9,10]" :key="h" :value="String(h)">{{ h }} PM</option>
                  </select>
                </div>
                <div style="font-size: 11px; color: #94a3b8; margin-top: 4px; line-height: 1.4;">
                  Sent shortly after this hour once all data is finalized.
                </div>
                <div class="er-schedule-time-row" style="margin-top: 8px;">
                  <span class="er-form-label-inline">TZ</span>
                  <select v-model="schedules[freq.key].Timezone" class="er-form-select" style="flex: 1;">
                    <option v-for="tz in timezoneOptions" :key="tz.value" :value="tz.value">{{ tz.text }}</option>
                  </select>
                </div>
              </div>
              <div class="er-schedule-card-body" style="color: #9ca3af; font-style: italic; font-size: 0.8rem;" v-else>
                Report disabled
              </div>
            </div>
          </div>
        </div>

        <!-- ─── INCLUDE IN REPORT ─── -->
        <div class="er-divider-section">
          <h3 class="er-subsection-title"><i class="fa fa-bar-chart"></i> Include in Report</h3>
          <p class="er-help-text" style="margin-bottom: 12px;">Select which sections to include in each report type.</p>

          <div class="er-table-wrapper">
            <table class="er-matrix-table">
              <thead>
                <tr>
                  <th class="er-matrix-label-col">Category</th>
                  <th v-for="freq in frequencies" :key="freq.key" class="er-matrix-freq-col">{{ freq.label.replace(' Report', '') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="cat in categoryRows" :key="cat.key">
                  <td class="er-matrix-label-cell">
                    <div class="er-cat-name">{{ cat.label }}</div>
                    <div class="er-cat-desc">{{ cat.description }}</div>
                  </td>
                  <td v-for="freq in frequencies" :key="freq.key" class="er-matrix-check-cell">
                    <template v-if="cat.availableFor.includes(freq.key)">
                      <input type="checkbox"
                        :checked="catChecks[freq.key + '__' + cat.key]"
                        :disabled="!schedules[freq.key].IsActive"
                        @change="toggleCategory(freq.key, cat.key)"
                      />
                    </template>
                    <span v-else style="color: #d1d5db;">—</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Tx Procedure Filters (universal) -->
          <div v-if="hasTxSelected" class="er-proc-filters">
            <h3 class="er-subsection-title" style="font-size: 0.8rem; margin-top: 16px;">
              <i class="fa fa-filter"></i> Tx Presented — Procedure Filters
            </h3>
            <p class="er-help-text" style="margin-bottom: 10px;">These selections apply to all report types that include Tx Presented vs Accepted.</p>
            <div class="er-proc-row">
              <span class="er-proc-freq-label">Type:</span>
              <multiselect
                v-model="procTracking.procedureTypes"
                :options="procedureTypeOptions"
                :multiple="true"
                label="ItemName"
                track-by="DefNum"
                placeholder="Search procedure types..."
                :close-on-select="false"
                :show-labels="false"
                class="er-proc-multiselect"
              />
            </div>
            <div class="er-proc-row" style="margin-top: 8px;">
              <span class="er-proc-freq-label">Code:</span>
              <multiselect
                v-model="procTracking.procedureCodes"
                :options="procedureCodeOptions"
                :multiple="true"
                :custom-label="procCodeLabel"
                track-by="ProcCode"
                placeholder="Search procedure codes..."
                :close-on-select="false"
                :show-labels="false"
                class="er-proc-multiselect"
              />
            </div>
          </div>
        </div>

        <!-- ─── REPORT RECIPIENTS ─── -->
        <div class="er-divider-section">
          <h3 class="er-subsection-title"><i class="fa fa-users"></i> Report Recipients</h3>
          <p class="er-help-text" style="margin-bottom: 12px;">Select which employees receive each report type.</p>

          <div v-for="freq in frequencies" :key="'recip-' + freq.key" class="er-recip-freq-row" v-show="schedules[freq.key].IsActive">
            <span class="er-recip-freq-label">{{ freq.label.replace(' Report', '') }}:</span>
            <multiselect
              v-model="recipientSelections[freq.key]"
              :options="employeeOptions"
              :multiple="true"
              :custom-label="empLabel"
              track-by="EmployeeID"
              :placeholder="'Select ' + freq.label.replace(' Report', '').toLowerCase() + ' recipients...'"
              :close-on-select="false"
              :show-labels="false"
              class="er-recip-multiselect"
            />
          </div>

          <!-- Alternate emails for selected employees -->
          <div v-if="selectedEmployeesList.length > 0" class="er-alt-emails">
            <h3 class="er-subsection-title" style="font-size: 0.8rem; margin-top: 16px;">
              <i class="fa fa-at"></i> Alternate Email <span style="font-weight: 400; color: #9ca3af;">(optional)</span>
            </h3>
            <p class="er-help-text" style="margin-bottom: 8px;">Leave blank to use the employee's Teamio login email.</p>
            <div v-for="emp in selectedEmployeesList" :key="'alt-' + emp.EmployeeID" class="er-alt-email-row">
              <span class="er-alt-email-name">{{ emp.Name }}</span>
              <input type="email"
                :value="altEmails[emp.EmployeeID] || ''"
                @input="$set(altEmails, emp.EmployeeID, $event.target.value)"
                :placeholder="emp.Username"
                class="er-form-input er-alt-email-input"
              />
            </div>
          </div>
        </div>

        <!-- ─── SAVE ─── -->
        <div class="er-save-row">
          <button class="er-btn er-btn-save" @click="handleSave" :disabled="saving">
            <i :class="saving ? 'fa fa-spinner fa-spin' : 'fa fa-save'"></i>
            {{ saving ? 'Saving...' : 'Save Email Report Settings' }}
          </button>
        </div>

        <!-- Report History -->
        <div v-if="reportLogs.length > 0" class="er-divider-section">
          <h3 class="er-subsection-title"><i class="fa fa-history"></i> Recent Reports Sent</h3>
          <b-table :items="reportLogs" :fields="logFields" small striped hover responsive>
            <template #cell(Status)="data">
              <b-badge :variant="getStatusVariant(data.value)">{{ data.value }}</b-badge>
            </template>
            <template #cell(SentAt)="data">
              {{ formatDate(data.value) }}
            </template>
          </b-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import Multiselect from 'vue-multiselect';
import axios from 'axios';
import moment from 'moment-timezone';

const FREQUENCIES = [
  { key: 'daily', label: 'Daily Report' },
  { key: 'weekly', label: 'Weekly Report' },
  { key: 'monthly', label: 'Monthly Report' }
];

const CATEGORIES = [
  { key: 'financials_patients', label: 'Financials and Patients', description: 'Net Production, Collections, New Patients MTD (with goal tracking) \u2022 Outstanding Claims aging \u2022 Today\'s Net Production, Adjustments, Write-offs \u2022 Metrics: Production, Schedule, Collection, Patients, Hygiene, Treatment Acceptance \u2022 Collections Summary (checks, cash, credit cards, financing) \u2022 Production by Provider \u2022 Adjustments Summary', availableFor: ['daily', 'weekly', 'monthly'] },
  { key: 'tx_presented_accepted', label: 'Tx Presented vs Accepted — Last 3 Months', description: 'Provider breakdown with Tx Planned, Scheduled, Completed, Acceptance % \u2022 Monthly comparison bar chart \u2022 All Procedures summary table with per-procedure-code and per-procedure-type breakdowns', availableFor: ['daily', 'weekly', 'monthly'] },
  { key: 'team_insights_followups', label: 'Team Insights — Patient Follow-ups', description: 'Overdue Recall + Unscheduled Treatment follow-up activity \u2022 Total follow-ups, appointments scheduled, net $ value, conversion rates \u2022 Breakdown by team member with Call vs Scheduled charts', availableFor: ['weekly', 'monthly'] },
  { key: 'team_insights_claims', label: 'Team Insights — Outstanding Claims', description: 'Outstanding claims follow-up activity \u2022 Total follow-ups, claims received, claim $ value, conversion rates \u2022 Breakdown by team member with Call vs Received charts', availableFor: ['weekly', 'monthly'] },
  { key: 'next_2_weeks_production', label: 'Next 2 Weeks — Net Scheduled Production', description: 'Day-by-day scheduled production for the next 2 working weeks \u2022 Per-provider breakdown with mini progress bars \u2022 Color-coded goal tracking (green/yellow/red)', availableFor: ['daily', 'weekly', 'monthly'] }
];

const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', text: 'Eastern Time (ET)' },
  { value: 'America/Chicago', text: 'Central Time (CT)' },
  { value: 'America/Denver', text: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', text: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', text: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', text: 'Hawaii Time (HT)' },
  { value: 'America/Barbados', text: 'Atlantic Time (AST)' },
  { value: 'Europe/Dublin', text: 'Ireland (IST)' },
  { value: 'Africa/Johannesburg', text: 'South Africa (SAST)' }
];

function defaultSchedule() {
  return { IsActive: false, hour: '5', minute: '00', Timezone: 'America/New_York' };
}

export default {
  name: 'EmailReportsTab',
  components: { Multiselect },

  data() {
    return {
      frequencies: FREQUENCIES,
      categoryRows: CATEGORIES,
      timezoneOptions: TIMEZONE_OPTIONS,
      schedules: {
        daily: defaultSchedule(),
        weekly: defaultSchedule(),
        monthly: defaultSchedule()
      },
      catChecks: {},
      procTracking: { procedureTypes: [], procedureCodes: [] },
      procedureTypeOptions: [],
      procedureCodeOptions: [],
      // Recipients: multiselect selections per frequency + alternate emails
      recipientSelections: { daily: [], weekly: [], monthly: [] },
      altEmails: {},
      // Employee options for multiselect (frozen, loaded once)
      employeeOptions: [],
      logFields: [
        { key: 'Frequency', label: 'Report Type' },
        { key: 'RecipientEmail', label: 'Sent To' },
        { key: 'Status', label: 'Status' },
        { key: 'SentAt', label: 'Date' }
      ]
    };
  },

  computed: {
    ...mapState('emailReports', ['configs', 'loading', 'saving', 'reportLogs']),

    hasTxSelected() {
      return FREQUENCIES.some(f => this.catChecks[f.key + '__tx_presented_accepted']);
    },

    // Unique list of employees selected in ANY frequency (for alt email section)
    selectedEmployeesList() {
      const seen = new Set();
      const result = [];
      for (const freq of ['daily', 'weekly', 'monthly']) {
        for (const emp of (this.recipientSelections[freq] || [])) {
          if (!seen.has(emp.EmployeeID)) {
            seen.add(emp.EmployeeID);
            result.push(emp);
          }
        }
      }
      return result;
    }
  },

  async mounted() {
    // Load site timezone
    const siteTimezone = this.$store.state.customer.settings?.GENERAL_TIMEZONE?.PropertyValue;
    if (siteTimezone) {
      for (const freq of ['daily', 'weekly', 'monthly']) {
        this.schedules[freq].Timezone = siteTimezone;
      }
    }

    // Load employee options (frozen — no deep reactivity)
    const employees = this.$store.state.customer.employees || [];
    this.employeeOptions = Object.freeze(
      employees.filter(e => e.Active === 1).map(e => ({
        EmployeeID: e.EmployeeID,
        Name: e.Name,
        Username: e.Username
      }))
    );

    // Load configs
    await this.$store.dispatch('emailReports/fetchConfigs');
    this.populateFromConfigs();

    // Load report logs
    await this.$store.dispatch('emailReports/fetchReportLogs');

    // Load procedure options
    await this.loadProcedureOptions();
  },

  methods: {
    ...mapActions('emailReports', ['fetchReportLogs']),

    populateFromConfigs() {
      if (!this.configs) return;
      for (const freq of ['daily', 'weekly', 'monthly']) {
        const config = this.configs[freq];
        if (config) {
          this.schedules[freq].IsActive = config.IsActive || false;
          this.schedules[freq].Timezone = config.Timezone || this.schedules[freq].Timezone;
          this.parseDeliveryTime(freq, config.DeliveryTime || '17:00');

          // Categories
          const cats = Array.isArray(config.Categories) ? config.Categories : [];
          for (const cat of cats) {
            this.$set(this.catChecks, freq + '__' + cat, true);
          }

          // Procedure tracking (shared — load from first config that has it)
          if (config.ProcedureTracking && this.procTracking.procedureTypes.length === 0) {
            this.procTracking.procedureTypes = config.ProcedureTracking.procedureTypes || [];
            this.procTracking.procedureCodes = config.ProcedureTracking.procedureCodes || [];
          }

          // Recipients → multiselect selections
          if (config.Recipients) {
            const selected = [];
            for (const r of config.Recipients) {
              const emp = this.employeeOptions.find(e => e.EmployeeID === r.EmployeeID);
              if (emp) {
                selected.push(emp);
                if (r.AlternateEmail) {
                  this.$set(this.altEmails, r.EmployeeID, r.AlternateEmail);
                }
              }
            }
            this.recipientSelections[freq] = selected;
          }
        }
      }
    },

    parseDeliveryTime(freq, time) {
      const [h, m] = (time || '17:00').split(':');
      let hour = parseInt(h);
      if (hour > 12) hour -= 12;
      if (hour === 0) hour = 12;
      this.schedules[freq].hour = String(hour);
      this.schedules[freq].minute = m || '00';
    },

    formatDeliveryTime(freq) {
      let hour = parseInt(this.schedules[freq].hour);
      if (hour !== 12) hour += 12;
      return `${String(hour).padStart(2, '0')}:00`;
    },

    toggleCategory(freq, catKey) {
      const k = freq + '__' + catKey;
      this.$set(this.catChecks, k, !this.catChecks[k]);
    },

    procCodeLabel({ ProcCode, Descript }) {
      return `${ProcCode} — ${Descript}`;
    },

    empLabel({ Name, Username }) {
      return `${Name} (${Username})`;
    },

    async loadProcedureOptions() {
      try {
        const siteID = this.$store.state.customer.siteID;
        if (!siteID) return;
        const hqSiteID = this.$store.state.customer.hqSiteID || siteID;
        const hqDb = `teamio_${hqSiteID}`;
        const [typesRes, codesRes] = await Promise.all([
          axios.post('/runquery', { sqlQuery: `SELECT DefNum, ItemName FROM \`${hqDb}\`.definition WHERE Category = 11 AND IsHidden = 0;` }),
          axios.post('/runquery', { sqlQuery: `SELECT ProcCode, Descript FROM \`${hqDb}\`.procedurecode pc;` })
        ]);
        this.procedureTypeOptions = Object.freeze(typesRes.data || []);
        this.procedureCodeOptions = Object.freeze(codesRes.data || []);
      } catch (err) { console.error('Error loading procedure options:', err); }
    },

    async handleSave() {
      try {
        this.$emit('saving', true);

        const schedulePayload = {};
        for (const freq of ['daily', 'weekly', 'monthly']) {
          schedulePayload[freq] = {
            IsActive: this.schedules[freq].IsActive,
            DeliveryTime: this.formatDeliveryTime(freq),
            Timezone: this.schedules[freq].Timezone
          };
        }

        const categoriesPayload = { daily: [], weekly: [], monthly: [] };
        for (const [key, val] of Object.entries(this.catChecks)) {
          if (!val) continue;
          const [freq, cat] = key.split('__');
          if (categoriesPayload[freq]) categoriesPayload[freq].push(cat);
        }

        const sharedProcData = {
          procedureTypes: this.procTracking.procedureTypes.map(t => ({ DefNum: t.DefNum, ItemName: t.ItemName })),
          procedureCodes: this.procTracking.procedureCodes.map(c => ({ ProcCode: c.ProcCode, Descript: c.Descript }))
        };
        const procTrackingPayload = {};
        for (const freq of ['daily', 'weekly', 'monthly']) {
          procTrackingPayload[freq] = this.catChecks[freq + '__tx_presented_accepted'] ? sharedProcData : null;
        }

        const recipientsPayload = [];
        const seen = new Set();
        for (const freq of ['daily', 'weekly', 'monthly']) {
          for (const emp of (this.recipientSelections[freq] || [])) {
            if (!seen.has(emp.EmployeeID)) {
              seen.add(emp.EmployeeID);
            }
            // Build per-employee frequency list
          }
        }
        // Build final recipients with frequency lists
        for (const empId of seen) {
          const empFreqs = ['daily', 'weekly', 'monthly'].filter(f =>
            (this.recipientSelections[f] || []).some(e => e.EmployeeID === empId)
          );
          const emp = this.employeeOptions.find(e => e.EmployeeID === empId);
          if (emp && empFreqs.length > 0) {
            recipientsPayload.push({
              EmployeeID: emp.EmployeeID,
              EmailAddress: emp.Username,
              Name: emp.Name,
              AlternateEmail: this.altEmails[emp.EmployeeID] || null,
              Frequencies: empFreqs
            });
          }
        }

        await this.$store.dispatch('emailReports/saveAllConfigs', {
          schedules: schedulePayload,
          categories: categoriesPayload,
          procedureTracking: procTrackingPayload,
          recipients: recipientsPayload
        });

        this.$toast.success('Settings saved successfully!');
      } catch (error) {
        console.error('Error saving:', error);
        this.$toast.error('Failed to save settings. Please try again.');
      } finally {
        this.$emit('saving', false);
      }
    },

    getStatusVariant(status) {
      switch (status) { case 'sent': return 'success'; case 'failed': return 'danger'; case 'queued': return 'warning'; default: return 'secondary'; }
    },
    formatDate(date) { return moment(date).format('MMM D, YYYY h:mm A'); }
  }
};
</script>

<style lang="scss" scoped>
$er-gray-50: #f9fafb;
$er-gray-100: #f3f4f6;
$er-gray-200: #e5e7eb;
$er-gray-300: #d1d5db;
$er-gray-500: #6b7280;
$er-gray-600: #4b5563;
$er-gray-700: #374151;
$er-blue-600: #2563eb;
$er-blue-700: #1d4ed8;

.er-settings { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif; }

.er-card { background: white; border-radius: 12px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); padding: 24px; }

.er-card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;
  h2 { display: flex; align-items: center; gap: 8px; margin: 0; font-size: 0.875rem; font-weight: 600; letter-spacing: 0.025em; color: $er-gray-700; }
}

.er-btn { padding: 8px 16px; border-radius: 6px; font-size: 0.875rem; font-weight: 600; display: inline-flex; align-items: center; gap: 8px; border: none; cursor: pointer; transition: all 0.15s; white-space: nowrap;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
}
.er-btn-save { background: $er-blue-600; color: white; &:hover:not(:disabled) { background: $er-blue-700; } }

.er-divider-section { margin-top: 24px; padding-top: 24px; border-top: 1px solid $er-gray-200; }
.er-subsection-title { font-size: 0.875rem; font-weight: 600; color: $er-gray-700; margin-bottom: 12px; display: flex; align-items: center; gap: 8px; }
.er-help-text { font-size: 0.8rem; color: $er-gray-500; margin-top: 4px; }

.er-form-label-inline { font-size: 0.75rem; font-weight: 600; color: $er-gray-600; margin-right: 6px; white-space: nowrap; }
.er-form-select { border: 1px solid $er-gray-300; border-radius: 6px; padding: 6px 8px; font-size: 0.8rem; height: 34px; outline: none; background: white; &:focus { border-color: $er-blue-600; box-shadow: 0 0 0 2px rgba($er-blue-600, 0.15); } }
.er-form-input { width: 100%; border: 1px solid $er-gray-300; border-radius: 6px; padding: 6px 10px; font-size: 0.8rem; outline: none; box-sizing: border-box; &:focus { border-color: $er-blue-600; box-shadow: 0 0 0 2px rgba($er-blue-600, 0.15); } &::placeholder { color: $er-gray-300; } }

.er-note-box { background: #fffbeb; border: 2px solid #f59e0b; border-radius: 8px; padding: 12px 16px; margin-bottom: 16px; font-size: 0.8rem; color: $er-gray-700;
  .er-note-header { font-size: 0.85rem; color: #92400e; margin-bottom: 6px; i { color: #f59e0b; } }
  p { margin: 0 0 4px 0; line-height: 1.5; &:last-child { margin-bottom: 0; } }
}

// Schedule
.er-schedule-row { display: flex; gap: 12px; }
.er-schedule-card { flex: 1; border: 1px solid $er-gray-200; border-radius: 8px; background: white; overflow: hidden; transition: opacity 0.2s; &.is-disabled { opacity: 0.5; background: $er-gray-50; } }
.er-schedule-card-top { display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; background: $er-gray-50; border-bottom: 1px solid $er-gray-200; }
.er-schedule-card-label { font-weight: 600; font-size: 0.85rem; color: $er-gray-700; }
.er-schedule-card-body { padding: 12px 14px; min-height: 80px; }
.er-schedule-time-row { display: flex; align-items: center; }
.er-time-sel { width: 52px !important; text-align: center; }
.er-time-colon { margin: 0 3px; font-weight: 600; color: $er-gray-600; }

.er-toggle { display: inline-flex; align-items: center; gap: 6px; cursor: pointer; font-size: 0.8rem; color: $er-gray-600; input { display: none; } }
.er-toggle-slider { width: 34px; height: 18px; background: $er-gray-300; border-radius: 9px; position: relative; transition: background 0.2s;
  &::after { content: ''; position: absolute; width: 14px; height: 14px; background: white; border-radius: 50%; top: 2px; left: 2px; transition: transform 0.2s; }
}
.er-toggle input:checked + .er-toggle-slider { background: #10b981; &::after { transform: translateX(16px); } }
.er-toggle-text { font-weight: 500; }

// Category matrix
.er-table-wrapper { overflow-x: auto; border: 1px solid $er-gray-200; border-radius: 8px; background: white; }
.er-matrix-table { width: 100%; border-collapse: collapse; font-size: 0.875rem;
  thead { background: $er-gray-50; th { padding: 12px 8px; text-align: center; font-weight: 600; font-size: 0.8rem; color: $er-gray-700; border-bottom: 1px solid $er-gray-200; white-space: nowrap; } }
  tbody { tr:hover { background: $er-gray-50; } tr:not(:last-child) td { border-bottom: 1px solid $er-gray-100; } td { padding: 10px 8px; text-align: center; color: $er-gray-700; } }
}
.er-matrix-label-col { text-align: left !important; padding-left: 16px !important; min-width: 280px; }
.er-matrix-label-cell { text-align: left !important; padding-left: 16px !important; }
.er-matrix-freq-col { width: 120px; min-width: 90px; }
.er-matrix-check-cell { input[type="checkbox"] { width: 16px; height: 16px; cursor: pointer; accent-color: #10b981; } }
.er-cat-name { font-weight: 600; font-size: 0.85rem; color: #1B2559; }
.er-cat-desc { font-size: 0.8rem; color: $er-gray-500; margin-top: 3px; line-height: 1.4; }

// Procedure filters
.er-proc-filters { margin-top: 20px; padding-top: 16px; border-top: 1px solid $er-gray-100; }
.er-proc-row { display: flex; align-items: flex-start; gap: 10px; }
.er-proc-freq-label { font-weight: 600; font-size: 0.8rem; color: $er-gray-700; min-width: 45px; padding-top: 8px; }
.er-proc-multiselect { flex: 1; font-size: 0.8rem; }

// Recipients
.er-recip-freq-row { display: flex; align-items: flex-start; gap: 10px; margin-bottom: 10px; }
.er-recip-freq-label { font-weight: 600; font-size: 0.8rem; color: $er-gray-700; min-width: 65px; padding-top: 8px; }
.er-recip-multiselect { flex: 1; font-size: 0.8rem; }

// Alternate emails
.er-alt-emails { margin-top: 4px; }
.er-alt-email-row { display: flex; align-items: center; gap: 12px; margin-bottom: 6px; }
.er-alt-email-name { font-size: 0.8rem; font-weight: 500; color: $er-gray-700; min-width: 140px; }
.er-alt-email-input { max-width: 300px; }

.er-save-row { margin-top: 24px; display: flex; justify-content: flex-end; }
</style>
